// New invoice / edit a draft / view a sent invoice.
//   /admin/invoices/edit                 new invoice
//   /admin/invoices/edit?companyId=...   new invoice for a company (from the Companies menu)
//   /admin/invoices/edit?id=...          a draft (editable) or a sent invoice (actions only)

import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { get, post } from "../../utils/apiManager.js";
import { money, date, pill, statusOf, card, th, td, btn, btnPrimary, input } from "./Index.js";

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const emptyLine = () => ({ description: "", quantity: 1, unitPrice: "" });
const companyLabel = (c) => c?.companyName || [c?.firstName, c?.lastName].filter(Boolean).join(" ") || c?.email || "";

const InvoiceEdit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const id = params.get("id");

  const [invoice, setInvoice] = useState(null);       // saved invoice (when editing / viewing)
  const [company, setCompany] = useState(null);       // { _id, companyName, email }
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [lines, setLines] = useState([emptyLine()]);
  const [taxRate, setTaxRate] = useState("0");
  const [daysUntilDue, setDaysUntilDue] = useState("15");
  const [notes, setNotes] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState(null);

  const editable = !invoice || invoice.status === "draft";

  const fill = (inv) => {
    setInvoice(inv);
    setCompany(inv.company ? { ...inv.company, _id: inv.companyId } : { _id: inv.companyId });
    setLines(inv.lines?.length ? inv.lines.map((l) => ({ description: l.description, quantity: l.quantity, unitPrice: l.unitPrice })) : [emptyLine()]);
    setTaxRate(String(inv.taxRate ?? 0));
    setDaysUntilDue(String(inv.daysUntilDue ?? 15));
    setNotes(inv.notes || "");
    setInternalNote(inv.internalNote || "");
  };

  useEffect(() => {
    if (id) {
      get(`/company/platform-invoices/${id}`).then((res) => {
        if (res?.status) fill(res.data);
        else setNotice({ ok: false, text: res?.message || "Invoice not found." });
      });
      return;
    }
    get("/company/platform-invoices/settings").then((res) => {
      if (res?.status) { setTaxRate(String(res.data.taxRate ?? 0)); setDaysUntilDue(String(res.data.daysUntilDue ?? 15)); }
    });
    const companyId = params.get("companyId");
    if (companyId) {
      get(`/company/${companyId}`).then((res) => {
        const c = res?.datas;
        if (c) setCompany({ _id: c._id, companyName: c.companyName, firstName: c.firstName, lastName: c.lastName, email: c.email });
      });
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Company search (Companies list API)
  useEffect(() => {
    if (!editable || company || search.trim().length < 2) { setResults([]); return undefined; }
    const t = setTimeout(() => {
      get(`/company?page=1&perPage=10&searchItem=${encodeURIComponent(search.trim())}&statusValue=`)
        .then((res) => setResults(res?.status ? res.data || [] : []));
    }, 300);
    return () => clearTimeout(t);
  }, [search, company, editable]);

  const amounts = lines.map((l) => round2((Number(l.quantity) || 0) * (Number(l.unitPrice) || 0)));
  const subtotal = round2(amounts.reduce((a, b) => a + b, 0));
  const tax = round2(subtotal * (Number(taxRate) || 0) / 100);
  const total = round2(subtotal + tax);

  const setLine = (i, field, value) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [field]: value } : l)));
  const body = () => ({
    companyId: company?._id,
    lines: lines.filter((l) => String(l.description).trim() || Number(l.unitPrice)),
    taxRate, daysUntilDue, notes, internalNote,
  });

  const save = async (thenSend) => {
    if (!company?._id) { setNotice({ ok: false, text: "Choose a company." }); return; }
    if (thenSend && !window.confirm(`Send this ${money(total)} invoice to ${companyLabel(company)}${company.email ? ` (${company.email})` : ""}? They will get an email with a link to pay.`)) return;
    setBusy(thenSend ? "send" : "save");
    let res = invoice?._id
      ? await post(`/company/platform-invoices/${invoice._id}`, body())
      : await post("/company/platform-invoices", body());
    if (res?.status && thenSend) {
      res = await post(`/company/platform-invoices/${res.data._id}/send`, {});
    }
    setBusy("");
    setNotice({ ok: !!res?.status, text: res?.message || "Something went wrong." });
    if (res?.status && res.data?._id) {
      const fresh = await get(`/company/platform-invoices/${res.data._id}`);
      if (fresh?.status) fill(fresh.data);
      if (!id) navigate(`/admin/invoices/edit?id=${res.data._id}`, { replace: true });
    }
  };

  const act = async (path, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(path);
    const res = await post(`/company/platform-invoices/${invoice._id}/${path}`, {});
    setBusy("");
    setNotice({ ok: !!res?.status, text: res?.message || "Something went wrong." });
    if (path === "delete" && res?.status) { navigate("/admin/invoices"); return; }
    const fresh = await get(`/company/platform-invoices/${invoice._id}`);
    if (fresh?.status) fill(fresh.data);
  };

  const label = { fontSize: 13, color: "#475569", fontWeight: 600 };

  return (
    <div style={{ padding: "24px 0", maxWidth: 980 }}>
      <button type="button" style={{ ...btn, marginBottom: 14 }} onClick={() => navigate("/admin/invoices")}>← All invoices</button>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <h3 style={{ fontSize: 20, fontWeight: 700, color: "#0a1628", margin: 0 }}>
          {invoice?.number ? `Invoice ${invoice.number}` : invoice ? "Draft invoice" : "New invoice"}
        </h3>
        {invoice && pill(statusOf(invoice))}
      </div>

      {notice && (
        <div style={{ ...card, padding: "10px 16px", marginBottom: 16, background: notice.ok ? "#ecfdf5" : "#fef2f2", color: notice.ok ? "#065f46" : "#991b1b" }}>{notice.text}</div>
      )}
      {invoice?.lastError && (
        <div style={{ ...card, padding: "10px 16px", marginBottom: 16, background: "#fffbeb", color: "#92400e" }}>⚠ {invoice.lastError}</div>
      )}

      <div style={{ ...card, padding: 18 }}>
        <div style={label}>Company</div>
        {company ? (
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 6 }}>
            <div>
              <div style={{ fontWeight: 600, color: "#0a1628" }}>{companyLabel(company) || "Company"}</div>
              <div style={{ fontSize: 13, color: "#64748b" }}>{company.email ? `Invoice will be emailed to ${company.email}` : ""}</div>
            </div>
            {editable && <button type="button" style={{ ...btn, padding: "4px 10px", fontSize: 12 }} onClick={() => { setCompany(null); setSearch(""); }}>Change</button>}
          </div>
        ) : (
          <div style={{ position: "relative", marginTop: 6, maxWidth: 460 }}>
            <input style={{ ...input, width: "100%" }} placeholder="Search by company name or email" value={search} onChange={(e) => setSearch(e.target.value)} />
            {results.length > 0 && (
              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 8px 24px rgba(15,23,42,.1)", zIndex: 20, maxHeight: 280, overflowY: "auto" }}>
                {results.map((c) => (
                  <button key={c._id} type="button" onClick={() => { setCompany(c); setResults([]); }}
                    style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 12px", border: "none", background: "#fff", cursor: "pointer", borderBottom: "1px solid #f1f5f9" }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: "#0a1628" }}>{companyLabel(c)}</div>
                    <div style={{ fontSize: 12, color: "#64748b" }}>{c.email}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div style={card}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Description</th>
                <th style={{ ...th, width: 100 }}>Qty</th>
                <th style={{ ...th, width: 140 }}>Price</th>
                <th style={{ ...th, width: 120, textAlign: "right" }}>Amount</th>
                {editable && <th style={{ ...th, width: 40 }}></th>}
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td style={td}>
                    {editable
                      ? <input style={{ ...input, width: "100%" }} value={l.description} placeholder="e.g. Facebook ad campaign — October" onChange={(e) => setLine(i, "description", e.target.value)} />
                      : l.description}
                  </td>
                  <td style={td}>
                    {editable
                      ? <input style={{ ...input, width: 80 }} type="number" min="0" step="any" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} />
                      : l.quantity}
                  </td>
                  <td style={td}>
                    {editable
                      ? <input style={{ ...input, width: 120 }} type="number" min="0" step="0.01" value={l.unitPrice} placeholder="0.00" onChange={(e) => setLine(i, "unitPrice", e.target.value)} />
                      : money(l.unitPrice)}
                  </td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{money(amounts[i])}</td>
                  {editable && (
                    <td style={td}>
                      {lines.length > 1 && <button type="button" title="Remove line" style={{ ...btn, padding: "2px 8px" }} onClick={() => setLines(lines.filter((_, j) => j !== i))}>✕</button>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 16, padding: 16 }}>
          <div>
            {editable && <button type="button" style={btn} onClick={() => setLines([...lines, emptyLine()])}>+ Add line</button>}
          </div>
          <div style={{ minWidth: 260 }}>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", color: "#475569" }}><span>Subtotal</span><span>{money(subtotal)}</span></div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0", color: "#475569", gap: 8 }}>
              <span>
                Sales tax{" "}
                {editable
                  ? <input style={{ ...input, width: 80, padding: "4px 8px" }} type="number" min="0" max="25" step="0.001" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} />
                  : `${taxRate}`}
                {" "}%
              </span>
              <span>{money(tax)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0 0", borderTop: "1px solid #e2e8f0", marginTop: 6, fontWeight: 700, fontSize: 16, color: "#0a1628" }}><span>Total</span><span>{money(total)}</span></div>
          </div>
        </div>
      </div>

      <div style={{ ...card, padding: 18, display: "grid", gap: 14 }}>
        <label style={label}>
          Days to pay{" "}
          {editable
            ? <input style={{ ...input, width: 90, marginLeft: 6 }} type="number" min="0" max="365" value={daysUntilDue} onChange={(e) => setDaysUntilDue(e.target.value)} />
            : <span style={{ fontWeight: 400 }}>{daysUntilDue}{invoice?.dueDate ? ` (due ${date(invoice.dueDate)})` : ""}</span>}
        </label>
        <label style={label}>
          Note to the company (on the invoice and in the email)
          {editable
            ? <textarea style={{ ...input, width: "100%", minHeight: 70, display: "block", marginTop: 6 }} value={notes} onChange={(e) => setNotes(e.target.value)} />
            : <div style={{ fontWeight: 400, whiteSpace: "pre-line", marginTop: 4 }}>{notes || "—"}</div>}
        </label>
        <label style={label}>
          Private note (only seen here)
          <textarea style={{ ...input, width: "100%", minHeight: 50, display: "block", marginTop: 6 }} value={internalNote} disabled={!editable} onChange={(e) => setInternalNote(e.target.value)} />
        </label>
      </div>

      {invoice && !editable && (
        <div style={{ ...card, padding: 18, fontSize: 14, color: "#3a4a6a", display: "grid", gap: 6 }}>
          <div>Sent {date(invoice.sentAt)}{invoice.emailedTo ? ` to ${invoice.emailedTo}` : ""}</div>
          {invoice.paidAt && <div>Paid {date(invoice.paidAt)}{invoice.paidOutsideStripe ? " (marked paid outside Stripe)" : " (on Stripe)"}</div>}
          {invoice.voidedAt && <div>Voided {date(invoice.voidedAt)}</div>}
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            {invoice.hostedInvoiceUrl && invoice.status === "open" && <a href={invoice.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer">Stripe pay page</a>}
            {invoice.invoicePdfUrl && <a href={invoice.invoicePdfUrl} target="_blank" rel="noopener noreferrer">PDF</a>}
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {editable && (
          <>
            <button type="button" style={btnPrimary} disabled={!!busy} onClick={() => save(true)}>{busy === "send" ? "Sending…" : "Save and send"}</button>
            <button type="button" style={btn} disabled={!!busy} onClick={() => save(false)}>{busy === "save" ? "Saving…" : "Save draft"}</button>
            {invoice?._id && <button type="button" style={{ ...btn, color: "#b91c1c" }} disabled={!!busy} onClick={() => act("delete", "Delete this draft?")}>Delete draft</button>}
          </>
        )}
        {invoice?.status === "open" && (
          <>
            <button type="button" style={btn} disabled={!!busy} onClick={() => act("resend")}>Email again</button>
            <button type="button" style={btn} disabled={!!busy} onClick={() => act("refresh")}>Check payment with Stripe</button>
            <button type="button" style={btn} disabled={!!busy} onClick={() => act("mark-paid", "Mark this invoice paid? Use this when the company paid some other way (check, cash). The Stripe pay link stops working.")}>Mark paid (outside Stripe)</button>
            <button type="button" style={{ ...btn, color: "#b91c1c" }} disabled={!!busy} onClick={() => act("void", "Void this invoice? The pay link stops working. This can't be undone.")}>Void</button>
          </>
        )}
      </div>
    </div>
  );
};

export default InvoiceEdit;
